import React from 'react'
import PropTypes from 'prop-types'
import { Divider, makeStyles } from '@material-ui/core'
import { noop } from 'lodash'
import clsx from 'clsx'
import { useBoolean } from '../../../../../hooks'
import Bookmark from '../../../../atoms/Bookmark'
import Text from '../../../../atoms/Text'
import CardSkeleton from '../../components/CardSkeleton'
import Tooltip from '../../../../atoms/Tooltip'
import FastAvatar from '../../../../molecules/FastAvatar'
import ClientCardFooter from './ClientCardFooter'

const alignToFlex = (align) => {
  if (align === 'left') return 'flex-start'
  if (align === 'right') return 'flex-end'
  return 'center'
}

const useStyles = makeStyles(() => ({
  container: ({ noPadding, fullWidth, autoWidth }) => ({
    backgroundColor: 'white',
    padding: noPadding ? '' : '1rem',
    display: 'flex',
    width: autoWidth ? 'auto' : '100%',
    flex: fullWidth ? 1 : (autoWidth ? '0 0 auto' : undefined),
    minWidth: fullWidth ? 0 : undefined
  }),
  content: ({ loading, contentAlign, flatten, fullWidth, autoWidth }) => ({
    position: 'relative',
    margin: 'auto',
    width: fullWidth ? '100%' : (autoWidth ? 'auto' : undefined),
    textAlign: contentAlign,
    height: '100%',
    alignItems: alignToFlex(contentAlign),
    borderRadius: 'var(--border-radius-lg)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    transition: 'border-color .25s ease-in-out, background-color .25s ease-in-out',
    '&:hover': loading ? {} : (
      flatten
        ? { cursor: 'pointer' }
        : { background: 'var(--theme-color-surface-d0)', border: '2px solid #141929', cursor: 'pointer' }
    )
  }),
  avatarWrap: ({ avatarAlign }) => ({
    display: 'flex',
    width: '100%',
    justifyContent: alignToFlex(avatarAlign)
  }),
  titleContainer: ({ contentAlign }) => ({
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: alignToFlex(contentAlign),
    width: '100%'
  }),
  title: { fontStyle: 'normal', fontSize: '1rem', lineHeight: '1.4', color: '#212945' },
  divider: { margin: '0.5rem 0 0.875rem 0', width: '60%', border: 'var(--border-neutral-thin)' },
  bookmark: { position: 'absolute', top: '0.5rem', right: '0.5rem' }
}))

const ClientCardItem = ({
  client,
  onClick,
  loading,
  footer,
  noPadding,
  tooltipTitle,
  disableHover,
  clientBookmarked,
  onBookmarkChange,
  fullWidth,
  autoWidth,
  contentAlign,
  flatten,
  showDivider,
  avatarAlign,

  // NEW: allows parent components to pass inline style overrides into ClientCardItem
  componentStyles
}) => {
  const classes = useStyles({
    loading,
    noPadding,
    fullWidth,
    autoWidth,
    contentAlign,
    flatten,
    avatarAlign
  })
  const [hovered, setHovered] = useBoolean()

  if (loading) {
    return (
      <div
        className={clsx(classes.container)}
        // NEW: styles loading card container
        style={componentStyles?.container}
      >
        <div
          className={classes.content}
          // NEW: styles loading card content wrapper
          style={componentStyles?.content}
        >
          <CardSkeleton />
        </div>
      </div>
    )
  }

  return (
    <Tooltip title={tooltipTitle}>
      <div
        className={classes.container}
        // NEW: styles outer card container
        style={componentStyles?.container}
      >
        <div
          className={classes.content}
          // NEW: styles inner clickable content wrapper
          style={componentStyles?.content}
          onMouseEnter={setHovered.on}
          onMouseLeave={setHovered.off}
          onClick={onClick}
        >
          {(hovered && !disableHover) || clientBookmarked ? (
            <div
              className={classes.bookmark}
              // NEW: styles bookmark wrapper
              style={componentStyles?.bookmark}
            >
              <Bookmark name={client?.clientId || ''} value={Boolean(clientBookmarked)} onChange={onBookmarkChange} />
            </div>
          ) : null}

          <div
            className={classes.avatarWrap}
            // NEW: styles avatar wrapper
            style={componentStyles?.avatarWrap}
          >
            <FastAvatar
              avatarUrl={client?.avatarUrl}
              abbreviation={client?.clientAbbreviation}
            />
          </div>

          <div
            className={classes.titleContainer}
            // NEW: styles title container
            style={componentStyles?.titleContainer}
          >
            <Text
              className={classes.title}
              text={client?.longName || client?.shortName}
              customFontWeight='bold'
              // NEW: styles Text component directly
              style={componentStyles?.title}
            />
          </div>

          {showDivider && (
            <Divider
              className={classes.divider}
              // NEW: styles divider
              style={componentStyles?.divider}
            />
          )}

          {footer || <ClientCardFooter client={client} isHovered={hovered} />}
        </div>
      </div>
    </Tooltip>
  )
}

ClientCardItem.propTypes = {
  client: PropTypes.object,
  onClick: PropTypes.func,
  loading: PropTypes.bool,
  footer: PropTypes.node,
  noPadding: PropTypes.bool,
  tooltipTitle: PropTypes.string,
  disableHover: PropTypes.bool,
  clientBookmarked: PropTypes.bool,
  onBookmarkChange: PropTypes.func,
  fullWidth: PropTypes.bool,
  autoWidth: PropTypes.bool,
  contentAlign: PropTypes.oneOf(['left', 'center', 'right']),
  flatten: PropTypes.bool,
  showDivider: PropTypes.bool,
  avatarAlign: PropTypes.oneOf(['left', 'center', 'right']),

  // NEW
  componentStyles: PropTypes.shape({
    container: PropTypes.object,
    content: PropTypes.object,
    bookmark: PropTypes.object,
    avatarWrap: PropTypes.object,
    titleContainer: PropTypes.object,
    title: PropTypes.object,
    divider: PropTypes.object
  })
}

ClientCardItem.defaultProps = {
  client: {},
  onClick: noop,
  loading: false,
  footer: undefined,
  noPadding: false,
  tooltipTitle: '',
  disableHover: false,
  clientBookmarked: false,
  onBookmarkChange: noop,
  fullWidth: false,
  autoWidth: false,
  contentAlign: 'center',
  flatten: false,
  showDivider: true,
  avatarAlign: 'center',

  // NEW
  componentStyles: undefined
}

export default ClientCardItem
